import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import {
  Check, Sparkles, Crown, ChefHat, Martini, BedDouble, Building2,
  ArrowUpRight, CreditCard, Receipt, AlertTriangle, Loader2, Lock,
} from "lucide-react";
import api, { formatApiError } from "../api";
import { useAuth } from "../contexts/AuthContext";
import { Button, Card, Badge, Spinner } from "../components/ui/Primitives";
import { formatEUR, formatEUR as fmtEUR } from "../utils/format";

const PLAN_ICONS = {
  kueche: ChefHat,
  bar: Martini,
  pro: Sparkles,
  hotel: BedDouble,
  premium: Crown,
};

const PLAN_ACCENT = {
  kueche: "from-orange-500/20 to-orange-500/0",
  bar: "from-purple-500/20 to-purple-500/0",
  pro: "from-blue-500/20 to-blue-500/0",
  hotel: "from-emerald-500/20 to-emerald-500/0",
  premium: "from-amber-400/30 to-amber-400/0",
};

function PlanCard({ plan, interval, current, recommended, onSelect, busy }) {
  const { t } = useTranslation();
  const Icon = PLAN_ICONS[plan.id] || Sparkles;
  const price = interval === "annual" ? plan.annual : plan.monthly;
  const isCurrent = current?.plan === plan.id;
  return (
    <div
      data-testid={`plan-card-${plan.id}`}
      className={`relative bg-bg-surface border rounded-2xl p-6 flex flex-col lift transition-all ${
        isCurrent
          ? "border-brand/60 shadow-glow"
          : recommended
          ? "border-brand/30"
          : "border-white/10 hover:border-white/20"
      }`}
    >
      {recommended && !isCurrent && (
        <div className="absolute -top-2.5 left-6 px-2 py-0.5 rounded-md bg-brand text-white text-[10px] uppercase tracking-wider font-semibold">
          {t("billing.recommended")}
        </div>
      )}
      {isCurrent && (
        <div className="absolute -top-2.5 left-6 px-2 py-0.5 rounded-md bg-ok text-white text-[10px] uppercase tracking-wider font-semibold">
          {t("billing.current_plan")}
        </div>
      )}
      <div className={`absolute -top-12 -right-12 w-40 h-40 rounded-full bg-gradient-to-br ${PLAN_ACCENT[plan.id] || ""} blur-3xl opacity-60 pointer-events-none`} />

      <div className="flex items-center justify-between mb-4 relative">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-brand">
            <Icon size={16} strokeWidth={1.6} />
          </div>
          <div className="text-base font-semibold text-ink-primary tracking-tight">{plan.label}</div>
        </div>
      </div>

      <div className="relative">
        <div className="flex items-baseline gap-1.5 mb-1">
          <span className="num text-3xl font-semibold text-ink-primary tracking-tight" data-testid={`plan-${plan.id}-price`}>
            {formatEUR(price)}
          </span>
          <span className="text-xs text-ink-muted">
            / {interval === "annual" ? t("billing.year") : t("billing.month")}
          </span>
        </div>
        <div className="text-[10px] text-ink-muted mb-1" data-testid={`plan-${plan.id}-vat`}>
          {t("billing.vat_notice")}
        </div>
        {interval === "annual" && plan.annual_savings_pct > 0 && (
          <div className="text-[11px] text-ok mb-3">{t("billing.annual_saving", { pct: plan.annual_savings_pct })}</div>
        )}
        {interval === "monthly" && (
          <div className="text-[11px] text-ink-muted mb-3">{t("billing.netto_hint")}</div>
        )}
      </div>

      <ul className="space-y-2 mt-2 mb-5 relative text-sm text-ink-secondary">
        <li className="flex items-start gap-2">
          <Check size={14} className="text-ok mt-0.5 shrink-0" />
          <span>{plan.monthly_credits} {t("billing.credits_per_month")}</span>
        </li>
        {plan.id === "enterprise" && (
          <li className="flex items-start gap-2" data-testid="plan-enterprise-unlimited-outlets">
            <Check size={14} className="text-ok mt-0.5 shrink-0" />
            <span className="font-medium text-ink-primary">{t("billing.unlimited_outlets", "Unbegrenzte Outlets")}</span>
          </li>
        )}
        {plan.id === "premium" && (
          <li className="flex items-start gap-2" data-testid="plan-premium-outlets">
            <Check size={14} className="text-ok mt-0.5 shrink-0" />
            <span>{t("billing.premium_outlets", "2 Outlets inkl., bis 5 möglich (+€199/Mo je zusätzlich)")}</span>
          </li>
        )}
        {plan.modules.map((m) => (
          <li key={m} className="flex items-start gap-2">
            <Check size={14} className="text-ok mt-0.5 shrink-0" />
            <span>{t(`billing.module.${m}`, m)}</span>
          </li>
        ))}
      </ul>

      <div className="mt-auto relative">
        {isCurrent ? (
          <Button variant="secondary" disabled className="w-full" data-testid={`plan-${plan.id}-current`}>
            <Check size={14} /> {t("billing.current_plan")}
          </Button>
        ) : (
          <Button
            variant={recommended ? "primary" : "secondary"}
            className="w-full"
            disabled={busy}
            onClick={() => onSelect(plan.id, interval)}
            data-testid={`plan-${plan.id}-select`}
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <ArrowUpRight size={14} />}
            {current?.plan && current.plan !== "none" ? t("billing.switch_plan") : t("billing.subscribe")}
          </Button>
        )}
      </div>
    </div>
  );
}

function TopupCard({ topup, busy, onBuy }) {
  const { t } = useTranslation();
  return (
    <div
      data-testid={`topup-card-${topup.sku}`}
      className="bg-bg-surface border border-white/10 rounded-xl p-5 flex flex-col gap-2 hover:border-white/20 transition-colors"
    >
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider text-ink-muted">{t("billing.topup")}</div>
        <Sparkles size={14} className="text-brand" />
      </div>
      <div className="num text-2xl font-semibold text-ink-primary tracking-tight">
        {topup.credits}
      </div>
      <div className="text-xs text-ink-secondary">{t("billing.credits")}</div>
      <div className="flex items-end justify-between mt-2">
        <div>
          <div className="num text-lg text-ink-primary">{formatEUR(topup.price)}</div>
          <div className="text-[10px] text-ink-muted" data-testid={`topup-${topup.sku}-vat`}>
            {t("billing.vat_notice")}
          </div>
        </div>
        <Button
          variant="secondary"
          size="sm"
          disabled={busy}
          onClick={() => onBuy(topup.sku)}
          data-testid={`topup-${topup.sku}-buy`}
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : t("billing.buy")}
        </Button>
      </div>
    </div>
  );
}

export default function AboBillingPage() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const location = useLocation();
  const [plans, setPlans] = useState([]);
  const [topups, setTopups] = useState([]);
  const [interval, setInterval] = useState("monthly");
  const [busyPlan, setBusyPlan] = useState(null);
  const [busyTopup, setBusyTopup] = useState(null);
  const [busyPortal, setBusyPortal] = useState(false);
  const [busyCancel, setBusyCancel] = useState(false);
  const [loading, setLoading] = useState(true);

  const upgradeMod = useMemo(() => new URLSearchParams(location.search).get("upgrade"), [location.search]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/billing/plans");
        if (!alive) return;
        setPlans(data.plans || []);
        setTopups(data.topups || []);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const [previewState, setPreviewState] = useState(null); // {plan, interval, summary, kind, amount_due_today_eur}
  const [additionalOutlets, setAdditionalOutlets] = useState(0); // Phase 25 — Premium add-on

  const handleSubscribe = async (planId, intv) => {
    // Step 1: fetch proration preview from Stripe (skip for new sign-ups w/o sub)
    setBusyPlan(planId + "-" + intv);
    try {
      const { data: preview } = await api.get("/billing/proration-preview", {
        params: { plan: planId, interval: intv },
      });
      setPreviewState({ ...preview, plan: planId, interval: intv });
    } catch (e) {
      // Fall back to direct checkout if preview fails (e.g. user has no sub yet & Stripe issues)
      toast.error(formatApiError(e));
    } finally {
      setBusyPlan(null);
    }
  };

  const confirmPlanChange = async () => {
    if (!previewState) return;
    setBusyPlan(previewState.plan + "-" + previewState.interval);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/billing/checkout", {
        plan: previewState.plan, interval: previewState.interval, origin_url: origin,
        additional_outlets: previewState.plan === "premium" ? additionalOutlets : 0,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBusyPlan(null);
    }
  };

  const handleTopup = async (sku) => {
    setBusyTopup(sku);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/billing/topup-checkout", {
        sku, origin_url: origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyTopup(null);
    }
  };

  const handlePortal = async () => {
    setBusyPortal(true);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/billing/portal", { origin_url: origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyPortal(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm(t("billing.cancel_confirm_v2", "Abo zum Ende des aktuellen Abrechnungszeitraums kündigen? Der Zugriff bleibt bis dahin bestehen, danach wird das Konto deaktiviert. Keine Rückerstattungen."))) return;
    setBusyCancel(true);
    try {
      const { data } = await api.post("/billing/cancel");
      const endDate = data.access_until ? new Date(data.access_until).toLocaleDateString("de-AT") : "—";
      toast.success(`Abo gekündigt. Zugriff bis ${endDate}.`);
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyCancel(false);
    }
  };

  const totalCredits = (user?.ai_credits || 0) + (user?.purchased_credits || 0);
  const hasActiveSub = !!user?.subscription_status && user.plan && user.plan !== "none";

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Spinner size={28} />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto" data-testid="billing-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-ink-muted font-medium mb-1.5">
            {t("nav.billing")}
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-ink-primary" data-testid="billing-title">
            {t("billing.title")}
          </h1>
          <p className="text-sm text-ink-secondary mt-2 max-w-2xl">{t("billing.subtitle")}</p>
        </div>
      </div>

      {upgradeMod && (
        <div className="mb-6 flex items-center gap-3 p-4 rounded-xl border border-warn/30 bg-warn/10 text-warn text-sm" data-testid="upgrade-banner">
          <Lock size={16} />
          <span>{t("billing.upgrade_required_for", { module: t(`billing.module.${upgradeMod}`, upgradeMod) })}</span>
        </div>
      )}

      {/* Current subscription summary */}
      <Card className="p-5 mb-6" data-testid="current-subscription">
        <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-ink-muted mb-1">{t("billing.your_plan")}</div>
            <div className="text-lg font-medium text-ink-primary capitalize" data-testid="current-plan-label">
              {user?.plan && user.plan !== "none" ? user.plan : t("billing.no_plan")}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-ink-muted mb-1">{t("billing.status")}</div>
            <div className="text-sm">
              {user?.subscription_status ? (
                <Badge tone={user.subscription_status === "active" ? "ok" : user.subscription_status === "past_due" ? "warn" : "default"}>
                  {user.subscription_status}
                </Badge>
              ) : (
                <span className="text-ink-muted text-sm">—</span>
              )}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-ink-muted mb-1">{t("billing.credits")}</div>
            <div className="text-sm text-ink-primary" data-testid="current-credits">
              <span className="num">{totalCredits}</span>
              <span className="text-ink-muted ml-1">
                ({user?.ai_credits ?? 0} Plan + {user?.purchased_credits ?? 0} Top-up)
              </span>
            </div>
          </div>
          {user?.current_period_end && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-ink-muted mb-1">{t("billing.next_renewal")}</div>
              <div className="text-sm text-ink-primary num">
                {new Date(user.current_period_end).toLocaleDateString("de-AT")}
              </div>
            </div>
          )}
          {user?.cancel_at && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-ink-muted mb-1">{t("billing.access_until", "Zugriff bis")}</div>
              <div className="text-sm text-warn num" data-testid="billing-access-until">
                {new Date(user.cancel_at).toLocaleDateString("de-AT")}
              </div>
              <div className="text-[10px] text-ink-muted mt-0.5">Ihr Abo endet am {new Date(user.cancel_at).toLocaleDateString("de-AT")}.</div>
            </div>
          )}
          <div className="ml-auto flex items-center gap-2">
            {hasActiveSub && (
              <>
                <Button variant="secondary" size="sm" onClick={handlePortal} disabled={busyPortal} data-testid="open-portal">
                  {busyPortal ? <Loader2 size={12} className="animate-spin" /> : <Receipt size={12} />}
                  {t("billing.invoices_and_methods")}
                </Button>
                {!user?.cancel_at && user?.role !== "super_admin" && (
                  <Button variant="danger" size="sm" onClick={handleCancel} disabled={busyCancel} data-testid="cancel-subscription">
                    {busyCancel ? <Loader2 size={12} className="animate-spin" /> : <AlertTriangle size={12} />}
                    {t("billing.cancel")}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </Card>

      {/* Interval toggle */}
      <div className="flex items-center justify-center mb-6">
        <div className="inline-flex bg-bg-surface border border-white/10 rounded-lg p-1" data-testid="interval-toggle">
          <button
            onClick={() => setInterval("monthly")}
            className={`px-4 py-1.5 rounded-md text-xs font-medium transition-colors ${
              interval === "monthly" ? "bg-brand text-white" : "text-ink-secondary hover:text-ink-primary"
            }`}
            data-testid="toggle-monthly"
          >
            {t("billing.monthly")}
          </button>
          <button
            onClick={() => setInterval("annual")}
            className={`px-4 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center gap-1.5 ${
              interval === "annual" ? "bg-brand text-white" : "text-ink-secondary hover:text-ink-primary"
            }`}
            data-testid="toggle-annual"
          >
            {t("billing.annual")}
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-ok/20 text-ok">-15%</span>
          </button>
        </div>
      </div>

      {/* Plans */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-10">
        {plans.map((p) => (
          <PlanCard
            key={p.id}
            plan={p}
            interval={interval}
            current={user}
            recommended={p.id === "pro"}
            busy={busyPlan === `${p.id}-${interval}`}
            onSelect={handleSubscribe}
          />
        ))}
      </div>

      {/* Top-ups */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-ink-primary tracking-tight" data-testid="topups-title">
            {t("billing.topups_title")}
          </h2>
          <span className="text-xs text-ink-muted">{t("billing.topups_hint")}</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {topups.map((tp) => (
            <TopupCard key={tp.sku} topup={tp} busy={busyTopup === tp.sku} onBuy={handleTopup} />
          ))}
        </div>
      </div>

      <InvoicesSection />

      <div className="text-xs text-ink-muted leading-relaxed max-w-3xl">
        <CreditCard size={12} className="inline mr-1.5 -mt-0.5" />
        Kündigung jederzeit zum Ende des aktuellen Abrechnungszeitraums (monatlich oder jährlich). Bei Jahresabonnements keine Rückerstattung für die laufende Periode.
      </div>

      {previewState && (
        <ProrationPreviewModal
          preview={previewState}
          busy={busyPlan === previewState.plan + "-" + previewState.interval}
          additionalOutlets={additionalOutlets}
          setAdditionalOutlets={setAdditionalOutlets}
          onConfirm={confirmPlanChange}
          onCancel={() => setPreviewState(null)}
        />
      )}
    </div>
  );
}

function ProrationPreviewModal({ preview, busy, additionalOutlets, setAdditionalOutlets, onConfirm, onCancel }) {
  const isUpgrade = preview.kind === "upgrade";
  const isDowngrade = preview.kind === "downgrade";
  const isPremium = preview.plan === "premium";
  const tone = isUpgrade ? "brand" : isDowngrade ? "warn" : "ok";
  const base = preview.interval === "annual" ? 289 * 12 * 0.85 : 289;
  const perExtra = preview.interval === "annual" ? 199 * 12 * 0.85 : 199;
  const total = base + (additionalOutlets || 0) * perExtra;
  const intvLabel = preview.interval === "annual" ? "Jahr" : "Monat";
  return (
    <div className="fixed inset-0 z-50 bg-bg-void/80 backdrop-blur-sm flex items-center justify-center px-4" onClick={onCancel}>
      <div
        className="bg-bg-surface border border-white/10 rounded-2xl max-w-lg w-full p-6 shadow-glow space-y-5"
        onClick={(e) => e.stopPropagation()}
        data-testid="proration-preview-modal"
      >
        <div>
          <div className={`text-[10px] uppercase tracking-wider font-medium mb-1 ${
            tone === "brand" ? "text-brand" : tone === "warn" ? "text-warn" : "text-ok"
          }`}>
            {isUpgrade ? "Upgrade" : isDowngrade ? "Downgrade" : "Neues Abo"}
          </div>
          <h3 className="text-xl font-semibold text-ink-primary tracking-tight">
            Plan-Wechsel bestätigen
          </h3>
        </div>

        <div className={`rounded-lg border px-4 py-3 ${
          tone === "brand" ? "border-brand/30 bg-brand/[0.06]" :
          tone === "warn" ? "border-warn/30 bg-warn/[0.06]" :
          "border-ok/30 bg-ok/[0.06]"
        }`} data-testid="proration-summary">
          <p className="text-sm text-ink-primary leading-relaxed">
            {preview.summary_de}
          </p>
        </div>

        {isPremium && (
          <div className="rounded-lg border border-white/10 bg-bg-void/40 p-4 space-y-2" data-testid="outlet-quantity-selector">
            <div className="flex items-center justify-between">
              <div className="text-[11px] uppercase tracking-wider text-ink-muted">Zusätzliche Outlets</div>
              <span className="text-[11px] text-ink-secondary">2 inklusive · max. 5 gesamt</span>
            </div>
            <div className="flex items-center gap-1.5">
              {[0, 1, 2, 3].map((n) => (
                <button
                  key={n}
                  type="button"
                  data-testid={`outlet-qty-${n}`}
                  onClick={() => setAdditionalOutlets(n)}
                  className={`flex-1 py-2 rounded-md text-sm font-medium border transition-colors ${additionalOutlets === n ? "bg-brand/10 text-brand border-brand/40" : "bg-white/5 text-ink-secondary border-white/10 hover:border-brand/30"}`}
                >
                  +{n}
                </button>
              ))}
            </div>
            <div className="text-xs text-ink-secondary pt-1" data-testid="outlet-price-breakdown">
              Basis {fmtEUR(base)} + {additionalOutlets} Outlets × {fmtEUR(perExtra)} = <span className="text-ink-primary font-semibold">{fmtEUR(total)}/{intvLabel}</span>
            </div>
          </div>
        )}

        <dl className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <dt className="text-[10px] uppercase tracking-wider text-ink-muted mb-1">Heute zu zahlen</dt>
            <dd className="num text-lg font-medium text-ink-primary" data-testid="proration-amount-due">
              {preview.amount_due_today_eur > 0
                ? fmtEUR(preview.amount_due_today_eur)
                : fmtEUR(0)}
            </dd>
          </div>
          {preview.next_renewal && (
            <div>
              <dt className="text-[10px] uppercase tracking-wider text-ink-muted mb-1">Aktiv bis</dt>
              <dd className="num text-lg font-medium text-ink-primary" data-testid="proration-next-renewal">
                {new Date(preview.next_renewal).toLocaleDateString("de-AT")}
              </dd>
            </div>
          )}
        </dl>

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onCancel} disabled={busy} data-testid="proration-cancel">
            Abbrechen
          </Button>
          <Button onClick={onConfirm} disabled={busy} data-testid="proration-confirm">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />}
            {isUpgrade ? "Jetzt upgraden" : isDowngrade ? "Downgrade bestätigen" : "Weiter zu Stripe"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function InvoicesSection() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/billing/invoices");
        if (!alive) return;
        setItems(data.items || []);
      } catch (e) {
        if (alive) setError(formatApiError(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const fmt = (iso) => iso ? new Date(iso).toLocaleDateString("de-AT", { day: "2-digit", month: "short", year: "numeric" }) : "—";
  const fmtMoney = (v, cur) => `${formatEUR(v)} ${cur && cur !== "EUR" ? cur : ""}`.trim();
  const statusTone = (s) => s === "paid" ? "ok" : s === "open" ? "warn" : s === "void" || s === "uncollectible" ? "err" : "default";

  return (
    <div className="mb-8" data-testid="invoices-section">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-ink-primary tracking-tight">
          {t("billing.invoices_title")}
        </h2>
        <span className="text-xs text-ink-muted">{t("billing.invoices_hint")}</span>
      </div>
      <Card className="overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12"><Spinner size={20} /></div>
        ) : error ? (
          <div className="text-sm text-err px-5 py-6">{error}</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-ink-muted px-5 py-8 text-center" data-testid="invoices-empty">
            {t("billing.invoices_empty")}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="invoices-table">
              <thead className="bg-white/[0.02] text-[10px] uppercase tracking-wider text-ink-muted">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium">{t("billing.inv_number")}</th>
                  <th className="text-left px-4 py-2.5 font-medium">{t("billing.inv_date")}</th>
                  <th className="text-left px-4 py-2.5 font-medium">{t("billing.inv_desc")}</th>
                  <th className="text-right px-4 py-2.5 font-medium">{t("billing.inv_amount")}</th>
                  <th className="text-left px-4 py-2.5 font-medium">{t("billing.inv_status")}</th>
                  <th className="text-right px-4 py-2.5 font-medium">{t("common.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((inv) => (
                  <tr key={inv.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`invoice-row-${inv.id}`}>
                    <td className="px-4 py-3 num text-ink-primary font-medium">{inv.number}</td>
                    <td className="px-4 py-3 text-ink-secondary">{fmt(inv.created)}</td>
                    <td className="px-4 py-3 text-ink-secondary truncate max-w-[280px]">{inv.description || "—"}</td>
                    <td className="px-4 py-3 num text-right text-ink-primary">{fmtMoney(inv.amount_total, inv.currency)}</td>
                    <td className="px-4 py-3">
                      <Badge tone={statusTone(inv.status)}>{t(`billing.inv_status_${inv.status}`, inv.status)}</Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1.5">
                        {inv.invoice_pdf && (
                          <a href={inv.invoice_pdf} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-brand hover:underline" data-testid={`invoice-pdf-${inv.id}`}>
                            <Receipt size={12} /> PDF
                          </a>
                        )}
                        {inv.hosted_invoice_url && (
                          <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-ink-secondary hover:text-ink-primary" data-testid={`invoice-view-${inv.id}`}>
                            <ArrowUpRight size={12} /> {t("billing.inv_view")}
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
